import type {
  CommissionCategory,
  CommissionDeals,
} from '@travel-planner/shared/pricing';
import {
  COMMISSION_CATEGORIES,
  PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
  parseCommissionDeals,
  resolveCommissionRates,
} from '@travel-planner/shared/pricing';
import { z } from 'zod';

// XT-964 — the four commission categories as form text: '' = no rate of its
// own. Kept as strings so a half-typed "12." survives a render.
export type CommissionRateTexts = Record<CommissionCategory, string>;

export const COMMISSION_CATEGORY_LABELS: Record<CommissionCategory, string> = {
  Accommodation: 'Accommodation',
  Experiences: 'Experiences',
  Dining: 'Dining',
  Transportation: 'Transportation',
};

export const commissionRateTexts = (
  deals: CommissionDeals | null | undefined
): CommissionRateTexts => {
  const texts = {} as CommissionRateTexts;
  for (const category of COMMISSION_CATEGORIES) {
    const value = deals?.[category];
    texts[category] = typeof value === 'number' ? String(value) : '';
  }
  return texts;
};

const parseRate = (text: string): number | null | 'invalid' => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value) || value < 0 || value > 100) return 'invalid';
  return Math.round(value * 100) / 100;
};

/** The deal the texts describe, or the categories that are not 0–100. */
export const parseCommissionRateTexts = (
  texts: CommissionRateTexts
):
  | { ok: true; deals: CommissionDeals }
  | { ok: false; invalid: CommissionCategory[] } => {
  const deals: CommissionDeals = {};
  const invalid: CommissionCategory[] = [];
  for (const category of COMMISSION_CATEGORIES) {
    const parsed = parseRate(texts[category]);
    if (parsed === 'invalid') invalid.push(category);
    else if (parsed !== null) deals[category] = parsed;
  }
  return invalid.length > 0 ? { ok: false, invalid } : { ok: true, deals };
};

// A rate as typed: empty (no rate of its own) or 0–100.
const rateText = z.string().refine((text) => parseRate(text) !== 'invalid', {
  message: 'Enter 0 to 100',
});

export const commissionRateTextsSchema = z.object({
  Accommodation: rateText,
  Experiences: rateText,
  Dining: rateText,
  Transportation: rateText,
});

export const EMPTY_COMMISSION_RATE_TEXTS: CommissionRateTexts = {
  Accommodation: '',
  Experiences: '',
  Dining: '',
  Transportation: '',
};

/** The categories whose text is not a rate, for the fields' error. */
export const invalidCommissionCategories = (
  texts: CommissionRateTexts
): CommissionCategory[] => {
  const parsed = parseCommissionRateTexts(texts);
  return parsed.ok ? [] : parsed.invalid;
};

/** A rate per category; a missing category has none. */
export type CommissionRates = Partial<Record<CommissionCategory, number>>;

/** XT-1039 — what every category pays when nothing else is set. */
export const APPLICATION_DEFAULT_COMMISSION_RATES: CommissionRates =
  Object.fromEntries(
    COMMISSION_CATEGORIES.map((category) => [
      category,
      PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
    ])
  );

/**
 * XT-1039 — the organisation's rate per category: its category rate, else its
 * Agency Commission default; none when it has neither.
 */
type PricingAdjustmentsLike =
  | {
      agencyCommission?: { default?: number } | null;
      agencyCommissionByCategory?: unknown;
    }
  | null
  | undefined;

export const organizationCommissionRates = (
  pricingAdjustments: PricingAdjustmentsLike
): CommissionRates => {
  const byCategory = parseCommissionDeals(
    pricingAdjustments?.agencyCommissionByCategory
  );
  const fallback = pricingAdjustments?.agencyCommission?.default;
  const rates: CommissionRates = {};
  for (const category of COMMISSION_CATEGORIES) {
    const rate = byCategory[category] ?? fallback;
    if (typeof rate === 'number') rates[category] = rate;
  }
  return rates;
};

/** The valid rates among typed texts; a half-typed or invalid one is left out. */
export const commissionRatesOfTexts = (
  texts: CommissionRateTexts
): CommissionRates => {
  const rates: CommissionRates = {};
  for (const category of COMMISSION_CATEGORIES) {
    const parsed = parseRate(texts[category]);
    if (typeof parsed === 'number') rates[category] = parsed;
  }
  return rates;
};

/**
 * XT-1039 — the rate agents are suggested, per category, by the same rule as the
 * itinerary pricing (`resolveCommissionRates`): the organisation's rate (else
 * the application default), raised by any higher deal given.
 */
export const suggestedCommissionRates = (
  pricingAdjustments: PricingAdjustmentsLike,
  deals: readonly CommissionRates[]
): CommissionRates =>
  resolveCommissionRates({
    org: {
      default: pricingAdjustments?.agencyCommission?.default,
      byCategory: parseCommissionDeals(
        pricingAdjustments?.agencyCommissionByCategory
      ),
    },
    deals,
  }).byCategory;
